import { gql } from '@apollo/client'

export const STORE_SETTINGS_FIELDS = gql`
  fragment StoreSettingsFields on StoreSettings {
    allowedCustomerType
    blockCheckoutOnLiveLimitFailure
    calculateLimitsBasedOnMedIdIssueDate
    calculateLimitsOnDaysLeft
    exclusivePurchaseLimitsMed
    exclusivePurchaseLimitsRec
    clearCartOnPrintOrderTicket
    cumulativeTaxes
    daysUntilZeroQuantityInventoryIsIgnored
    daysBeforeFinishingMetrcPackages
    dealStacking
    deliveryProvider @client
    enablePotencyWarningsForMedicalPurchaseLimits
    exitLabelExclusionByCategory
    guestCheckout
    guestCheckoutSkipNameInput
    inStoreFulfillment
    integrations {
      integrationType
      integratorId
      integratorsTypeId
    }
    inventoryRegulatoryIdMustBeUnique
    isSandbox
    disableGenerateGiftCardSku
    liveCheckin
    liveSalesPush
    importLabTestData
    lookupExternalRegulatoryIds
    medCustomersCanBeCheckedInAsRec
    medCustomersCanPurchaseRecInventoryCheckedInAsMed
    paymentTenderAeropayEnabled
    paymentTenderCreditEnabled
    paymentTenderOtherEnabled
    printBulkLabel
    printExitLabels
    printReceiptsOptions
    printCustomerNoteOnFulfillmentTicket
    pushInventoryRoomToMetrc
    recheckCustomerId
    regulatedCartMinPrice
    regulatedItemMinPrice
    regulatorAuthUserOverride
    regulatorId
    regulatorInventoryType
    requireConsentForCustomerDataRetention
    storeId
    timeZone
    useOutTheDoorPricing
    veriscanEnabled
    greetEnabled
    stashEnabled
    publicApiOnlyShowsPreTaxPrices
    disableRegulatorForRecDelivery
    disableRegulatorForMedDelivery
    bypassPinOnInlineDiscounts
    showPreviousPurchasesFromActiveStoreOnly
    enableTransporterOnDelivery
    userIdForComplianceJobs
    isLoungeStore
    dealStackingLoyaltyAlwaysStacks
    complianceAutofixDeliveries
    complianceAutofixSales
    complianceAutoreportSales
    enableOnHoldPackages
    flagOverLimitInventory
    includeTipsInDrawerCounts
    autoEnrollLoyaltyMedCustomers
    autoEnrollLoyaltyRecCustomers
    autoRetainCustomerData
    fieldsRequiredToEnrollInLoyalty
    limitCustomerPurchasesByCategory
    ecommEnabled
    kioskEnabled
    allowCartTotalsToBeRoundedUp
    roundCartTotalFeeName
    enterDrawerTotalOnOpenAndClose
    blockMedCustomersUnder18
    onlyPrintExitLabelsSoldByWeight
    playSoundOnOrderAheadPage
    storeOperatingHours {
      dayOfWeek
      openTime
      closeTime
      closeTimeIsNextDay
    }
    requirePinAfterInactivity
    returnItemsToInventoryByDefault
    addWholesaleCostToInventoryOnImport
    minimumMedCustomerAge
    packBulkIntoAny
  }
`

export const CORE_STORE_FIELDS = gql`
  ${STORE_SETTINGS_FIELDS}
  fragment StoreFields on Store {
    id
    clientId
    name
    city
    address
    zipcode
    state
    latitude
    longitude
    isOnboarding
    meta
    rooms {
      id
      storeId
      name
      isForSale
      isQuarantine
    }
    licenses {
      storeId
      license
      licenseType
      meta
    }
    settings {
      ...StoreSettingsFields
    }
    pobSettings {
      key
      provider
      enabled
      fields
    }
    achSettings {
      provider
      enabled
      fields
      processorTypes
    }
  }
`

export const STORES_DATA = gql`
  ${CORE_STORE_FIELDS}
  query GetStores($state: String) {
    stores(state: $state) {
      ...StoreFields
    }
  }
`

export const GET_STORES_ID_NAME = gql`
  query GetStoresForCustomerSidebar {
    stores {
      id
      name
    }
  }
`

export const GET_TRANSFER_STORES = gql`
  query GetStores($state: String) {
    stores(state: $state) {
      id
      name
      state
    }
  }
`

const CORE_STORE_FIELDS_SB = gql`
  ${STORE_SETTINGS_FIELDS}
  fragment StoreFieldsSideBar on Store {
    id
    name
    clientId
    city
    address
    zipcode
    state
    isOnboarding
    meta
    licenses {
      license
      licenseType
    }
    settings {
      ...StoreSettingsFields
    }
    pobSettings {
      key
      provider
      enabled
      fields
    }
    achSettings {
      provider
      enabled
      fields
      processorTypes
    }
  }
`

export const GET_STORES = gql`
  ${CORE_STORE_FIELDS_SB}
  query GetStores($state: String) {
    stores(state: $state) {
      ...StoreFieldsSideBar
    }
  }
`

export const CREATE_STORE = gql`
  ${CORE_STORE_FIELDS_SB}
  mutation CreateStore($store: NewStore!) {
    createStore(store: $store) {
      ...StoreFieldsSideBar
    }
  }
`

export const UPDATE_STORE = gql`
  ${CORE_STORE_FIELDS_SB}
  mutation UpdateStore($id: ID!, $store: StoreUpdate!) {
    updateStore(id: $id, store: $store) {
      ...StoreFieldsSideBar
    }
  }
`

export const UPDATE_STORES = gql`
  ${CORE_STORE_FIELDS_SB}
  mutation UpdateStores($stores: [StoreUpdate!]) {
    updateStores(stores: $stores) {
      ...StoreFieldsSideBar
    }
  }
`

export const GET_STORE_OPERATING_HOURS = gql`
  query GetStoreOperatingHours($storeId: ID!) {
    storeOperatingHours(storeId: $storeId) {
      storeId
      dayOfWeek
      openTime
      closeTime
      closeTimeIsNextDay
    }
  }
`

export const UPDATE_STORE_OPERATING_HOURS = gql`
  mutation UpdateStoreOperatingHours(
    $id: ID!
    $storeOperatingHours: [StoreOperatingHoursInput!]!
  ) {
    updateStoreOperatingHours(
      id: $id
      storeOperatingHours: $storeOperatingHours
    ) {
      storeId
      dayOfWeek
      openTime
      closeTime
      closeTimeIsNextDay
    }
  }
`

export const SET_STORE_ONBOARDING = gql`
  mutation SetStoreOnboarding($storeId: ID!, $enabled: Boolean!) {
    setStoreOnboarding(storeId: $storeId, enabled: $enabled) {
      id
      clientId
      name
      isOnboarding
    }
  }
`

// vendors are stores to transfer inventory to (originally desired for external transfers) - this is regulator sepcific so the regulator entity will use this
export const GET_VENDORS = gql`
  query GetVendors {
    vendors {
      license
      name
      regulatoryId
      type
      address
      city
      state
      zip
    }
  }
`
